import { useEffect, useState } from 'react'

/**
 * Whether the viewport is at or above the breakpoint where boards change shape.
 *
 * Asked in JavaScript because the arrow-key cursor has to move by the same
 * number of columns the CSS is drawing. Anything that guesses will be right on
 * a phone and diagonal on a laptop.
 *
 * Falls back to "narrow" wherever matchMedia is missing — an old browser, or a
 * test environment. Narrow is the safer wrong answer: the cursor moves by four
 * on a wider board, which is still a straight line down a column, where
 * guessing wide on a narrow board would run off the end of every row.
 */
export function useIsWide(query: string): boolean {
  const [wide, setWide] = useState(() => matches(query))

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined

    const media = window.matchMedia(query)
    const update = (): void => setWide(media.matches)

    update()

    // Safari only grew addEventListener on MediaQueryList in 14, and her
    // tablet is not necessarily new.
    if (typeof media.addEventListener === 'function') {
      media.addEventListener('change', update)
      return () => media.removeEventListener('change', update)
    }

    media.addListener(update)
    return () => media.removeListener(update)
  }, [query])

  return wide
}

function matches(query: string): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false
  return window.matchMedia(query).matches
}
